import { ItemState } from '@mimir/common/dto/itemState';
import { isPlaceholderItem } from '@mimir/common/util/item';
import type { Fraction } from '@mimir/common/util/fractionType';
import { approximateFractionValue, formatFractionAsExpression } from '@mimir/common/util/integerMath';
import { premiereTicksPerSecond } from '@mimir/primitive/nle/sequence';
import type { ClientItem } from '@mimir/common/dto/searchResultHit';
import { getMediaFrameRateFromItem } from '@mimir/common/util/playableItemUtils';
import { tryGetPreciseFrameRateFromApproximation } from '@mimir/common/util/framerate';
import { isDropFrameTc } from '@mimir/common/util/timecode';
import { dropFrameRateToDisplayFormat, type PremiereDropFrameTimecodeDisplayFormat } from '@mimir/primitive/nle/timecode';
import type { NleMimirItemInfo } from '../fileSync/premiereTypes';

export const getPremiereSubclipTimeRangeInSeconds = (timeInMs: number, offset: number = 0) => {
  return (timeInMs + offset) / 1000;
};

export type ViewSize = 'small' | 'large';

export const isPremiereMimirItemInfoPlaceholder = (item: NleMimirItemInfo | null | undefined): boolean => {
  return isPlaceholderItem({
    ...item,
    proxy: null,
    itemState: item?.itemState ?? ItemState.NEW
  });
};

/**
 * The eight specific colors allowed on a marker in Premiere.
 * These were taken from the Edit Marker dialog of Premiere 2023.
 * The hex code includes alpha suffix "FF" to match with Vuetify color picker component.
 * The order of the colors is important, since the Premiere API takes the index of
 * the color in this array when setting the color of a marker.
 */
export const premiereMarkerColors = [
  '#718637FF',
  '#D22C36FF',
  '#AF8BB1FF',
  '#E96F24FF',
  '#D0A12BFF',
  '#FFFFFFFF',
  '#428DFCFF',
  '#19F4D6FF',
];

/**
 * Converts time in seconds as fraction to Premiere 'ticks' format.
 */
export function convertSecondsToTicks(seconds: Fraction): string {
  // Since for things like in-points on clips we always target the frame boundary, the Math.ceil is necessary to ensure
  // that the in-point doesn't mistakenly fall on the previous frame.
  const ticks = Math.ceil(approximateFractionValue(seconds) * premiereTicksPerSecond);
  return ticks.toString();
}

export function getDropFrameTimecodeDisplayFormat(item: Pick<ClientItem, 'mediaFrameRate' | 'technicalMetadata' | 'timecode'>): PremiereDropFrameTimecodeDisplayFormat | null {
  if (!item.timecode) return null;

  const isDropFrame = isDropFrameTc(item.timecode);
  if (!isDropFrame) return null;

  const mediaFrameRate = getMediaFrameRateFromItem(item);
  if (mediaFrameRate == null) return null;

  const frameRate = tryGetPreciseFrameRateFromApproximation(mediaFrameRate);
  if (!frameRate) return null;
  const fractionAsExpression = formatFractionAsExpression(frameRate);
  return dropFrameRateToDisplayFormat[fractionAsExpression as keyof typeof dropFrameRateToDisplayFormat] ?? null;
}
